import axios from 'axios';
import { v4 as uuidv4 } from 'uuid';
import store from '@/store';
import CustomerResponseData from './Dto/CustomerResponseData';
import { router } from '@/main';

const baseURL = `${process.env.VUE_APP_API_URL}`;

const getAuthHeader = (token) => {
    const devMode = store.state.user.devMode;

    if (devMode) {
        return { 'X-API-KEY': `${token}` };
    } else {
        return { Authorization: `Bearer ${token}` };
    }
};

const apiRequest = async (method, url, data = null, params = {}, config = {}) => {
    if (store.getters['user/isInvalidLoggedInAccount']) {
        console.error('Account ID is not set');
        store.dispatch('user/logout');
        if (router.currentRoute.value.path !== '/') {
            router.push({ name: 'Login', query: { logout: 'true' } });
        }
        return;
    }
    try {
        return store.dispatch('user/apiCall', async (token) => {
            const requestConfig = {
                method: method,
                url: `${baseURL}/accounts/${store.state.user.user.account_id}/${url}`,
                headers: getAuthHeader(token),
                params: { ...params },
                data: data,
                ...config, // Merge additional config (including signal or cancelToken)
            };
            return await axios(requestConfig);
        });
    } catch (error) {
        throw error;
    }
};

/**
 * Repository for Customer API operations.
 * @namespace
 */
const customerApiRepository = {
    /**
     * Fetch customer data by UUID.
     * @param {string} customerId - The UUID of the customer to fetch.
     * @param {string | null} phone - Optional phone number to filter customers.
     * @param {string} customerName
     * @returns {Promise<CustomerResponseData>} A promise that resolves to a CustomerResponseData object.
     * @throws {Error} If the API call fails or the response is invalid.
     */
    getCustomer: async (customerId, phone = null, customerName = 'Guest', abortController = null) => {
        try {
            let url = `customers/${customerId}?customerName=${customerName}`;
            if (phone) {
                url += `&phone=${phone}`;
            }
            const config = abortController ? { signal: abortController.signal } : {};
            const response = await apiRequest('GET', url, null, {}, config);

            const customerData = response.data;

            return new CustomerResponseData.Builder()
                .withUuid(customerData.uuid)
                .withExternalId(customerData.external_id)
                .withFullName(customerData.full_name)
                .withFirstName(customerData.first_name)
                .withLastName(customerData.last_name)
                .withEmail(customerData.email)
                .withPhone(customerData.phone)
                .withPhoneWithoutPrefix(customerData.phone_without_prefix)
                .withAccount(customerData.account)
                .withNote(customerData.note)
                .withPlatformsInfo(customerData.platforms_info)
                .withConversations(customerData.conversations)
                .withMarketingConsent(customerData.marketing_consent)
                .withMarketingConsentUpdated(customerData.marketing_consent_updated)
                .withBusinessType(customerData.business_type)
                .withTags(customerData.tags)
                .withDateCreated(customerData.date_created)
                .withDateUpdated(customerData.date_updated)
                .withChannelType(customerData.channel_type)
                .withLanguage(customerData.language)
                .withCountry(customerData.country)
                .withWhatsappUserId(customerData.bsuid)
                .build();
        } catch (error) {
            console.error('Error fetching customer data: ', error);
            throw error;
        }
    },

    /**
     * Update customer fields
     * @param {string} customerId - The UUID of the customer to update.
     * @param {Object} updateData - Partial fields to update (e.g., { marketing_consent: true }).
     * @returns {Promise<Object>} API response
     */
    updateCustomer: async (customerId, updateData) => {
        try {
            const response = await apiRequest('POST', `customers/${customerId}`, updateData);
            return response?.data ?? response;
        } catch (error) {
            console.error('Error updating customer: ', error);
            throw error;
        }
    },

    /**
     * Update marketing subscription (consent) for a customer
     * @param {string} customerId
     * @param {boolean} subscribed
     * @returns {Promise<Object>} API response
     */
    updateMarketingSubscription: async (customerId, subscribed) => {
        try {
            const response = await apiRequest('POST', `customers/${customerId}/update-marketing-subscription`, {
                subscribed,
            });
            return response?.data ?? response;
        } catch (error) {
            throw error;
        }
    },

    /**
     * Fetch paginated list of customers
     * @param {number} page - Page number (0-indexed)
     * @param {number} size - Number of items per page
     * @param {string} search - Optional search query
     * @param {axios.CancelToken | null} cancelToken - Optional cancel token for cancelling requests
     * @returns {Promise<Object>} API response with paginated customer list
     */
    getCustomers: async (page = 0, size = 20, search = '', cancelToken = null) => {
        try {
            const params = {
                page,
                size,
            };
            if (search) {
                params.search = search;
            }
            const config = cancelToken ? { cancelToken } : {};
            const response = await apiRequest('GET', 'customers/find', null, params, config);
            return response?.data ?? response;
        } catch (error) {
            if (axios.isCancel(error)) {
                console.log('Request cancelled');
                throw error;
            }
            console.error('Error fetching customers list: ', error);
            throw error;
        }
    },

    /**
     * Delete a customer by ID
     * @param {string} customerId - The UUID of the customer to delete.
     * @returns {Promise<Object>} API response
     */
    deleteCustomer: async (customerId) => apiRequest('DELETE', `customers/${customerId}`),

    /**
     * Los staging assets del cliente: donde el asistente guarda la cualificación.
     * @param {string} customerId
     * @returns {Promise<Array<Object>>}
     */
    getStagingAssets: async (customerId) => {
        const response = await apiRequest('GET', `customers/${customerId}/staging-assets`);
        return response?.data || [];
    },

    /**
     * Guarda a mano la cualificación de un cliente.
     *
     * No hay endpoint para escribir los campos de un lead: el pipeline nativo los saca de
     * aquí, así que un staging asset nuevo con el mapa completo dispara el mismo upsert que
     * usa el asistente y refresca el lead. Es un upsert por cliente: repetirlo actualiza
     * los campos, no duplica el lead.
     *
     * El cuerpo va en snake_case a propósito: los DTO del backend son
     * `@JsonNaming(SnakeCase)` y un `skillId` en camelCase se ignora en silencio, se queda
     * en null y devuelve un 500.
     *
     * Ojo: la etapa del lead se decide por estos campos (`stageByField`), así que rellenar
     * uno puede mover la tarjeta de columna — nunca hacia atrás.
     *
     * @param {string} customerId
     * @param {string} skillId - El `playbookSkillId` del board (la skill del CRM)
     * @param {Object<string, string|null>} data - Mapa COMPLETO de campos de cualificación
     */
    saveQualificationFields: async (customerId, skillId, data) => {
        const response = await apiRequest('POST', `customers/${customerId}/staging-assets`, {
            id: uuidv4(),
            skill_id: skillId,
            creation_status: 'IN_PROGRESS',
            data,
        });
        return response?.data;
    },

    /**
     * Count the total conversations linked to the given customers (exact count from the backend).
     * @param {string[]} customerIds - UUIDs of the customers.
     * @returns {Promise<number>} Total number of conversations that would be deleted.
     */
    countConversationsByCustomers: async (customerIds) => {
        // Backend request DTOs use snake_case (Dto @JsonNaming); apiRequest returns the axios response.
        const response = await apiRequest('POST', 'conversations/count-by-customers', { customer_ids: customerIds });
        return response?.data?.total ?? 0;
    },
};

export default customerApiRepository;
